import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { MobileAidShell } from '../components/aid/MobileAidShell';
import { DesktopAidShell } from '../components/aid/DesktopAidShell';
import {
  calculateTradeValue,
  estimateNetworth,
  estimateTaxRate,
  resolveMaxFill,
  MAX_SHIPMENT_MULTIPLIER,
} from '../constants/aid';
import { sendAid } from '../api/aid';
import { getKingdom } from '../api/magic';
import type { ResourceAmounts, SendAidResult } from '../types/aid';
import type { KingdomMember } from '../types/magic';
import type { Province } from '../types/province';

/**
 * SF-154: named prop type (ExploreShellProps/SciencesShellProps/ThieveryShellProps precedent) —
 * the estimatedTaxInfo memo's return shape.
 */
export interface EstimatedTaxInfo {
  taxRate: number;
  receivedGold: number;
  receivedFood: number;
  receivedSoldiers: number;
  receivedRunes: number;
}

/**
 * SF-154: named, fully-typed props shared by MobileAidShell/DesktopAidShell — same
 * ExploreShellProps/SciencesShellProps/ThieveryShellProps precedent. State stays hoisted here
 * (not per-shell) so a breakpoint flip mid-entry doesn't lose the in-progress form.
 */
export interface AidShellProps {
  province: Province;
  availableResources: ResourceAmounts;
  resourceAmounts: ResourceAmounts;
  handleResourceChange: (resource: keyof ResourceAmounts, value: number) => void;
  handleMaxFill: (resource: keyof ResourceAmounts) => void;
  handleClearAll: () => void;
  resourceResetKey: number;
  selectedTarget: string;
  setSelectedTarget: (value: string) => void;
  kingdomMembers: KingdomMember[];
  isLoadingKingdom: boolean;
  kingdomLoadError: string | null;
  loadKingdom: () => void;
  tradeValue: number;
  maxShipment: number;
  estimatedTaxInfo: EstimatedTaxInfo | null;
  hasResources: boolean;
  isOverLimit: boolean;
  isApproachingLimit: boolean;
  canSubmit: boolean;
  submitDisabledReason: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  handleSendAid: () => void;
  aidResult: SendAidResult | null;
}

export function Aid() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, updateProvince } = useProvince();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  // Kingdom members state (auto-loaded on mount)
  const [selectedTarget, setSelectedTarget] = useState('');
  const [kingdomMembers, setKingdomMembers] = useState<KingdomMember[]>([]);
  const [isLoadingKingdom, setIsLoadingKingdom] = useState(false);
  const [hasAutoLoadedKingdom, setHasAutoLoadedKingdom] = useState(false);
  const [kingdomLoadError, setKingdomLoadError] = useState<string | null>(null);

  // Resource amounts
  const [resourceAmounts, setResourceAmounts] = useState<ResourceAmounts>({
    gold: 0,
    food: 0,
    soldiers: 0,
    runes: 0,
  });
  const [resourceResetKey, setResourceResetKey] = useState(0);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [aidResult, setAidResult] = useState<SendAidResult | null>(null);

  // SF-154 (mirrors SF-149's Thievery split): mobile routes feedback through the app-wide toast
  // system; desktop keeps its existing inline messages.
  const reportError = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'failure');
    } else {
      setSubmitError(message);
    }
  };

  // Calculate available resources
  const availableResources = useMemo(() => {
    if (!province) return { gold: 0, food: 0, soldiers: 0, runes: 0 };
    return {
      gold: province.resources.gold,
      food: province.resources.food,
      soldiers: province.population.soldiers,
      runes: province.resources.runes,
    };
  }, [province]);

  // Calculate trade value
  const tradeValue = useMemo(() => {
    return calculateTradeValue(
      resourceAmounts.gold,
      resourceAmounts.food,
      resourceAmounts.soldiers,
      resourceAmounts.runes
    );
  }, [resourceAmounts]);

  // Estimate max shipment (networth × 4)
  const maxShipment = useMemo(() => {
    if (!province) return 0;

    const nw = estimateNetworth(
      province.resources.gold,
      province.population.peasants,
      province.population.soldiers,
      province.population.defenseSpecialists,
      province.population.offenseSpecialists,
      province.population.elites,
      province.population.thieves,
      province.population.wizards,
      province.territory.totals.barren,
      province.territory.totals.buildings,
      province.territory.totals.construction
    );

    return Math.round(nw * MAX_SHIPMENT_MULTIPLIER);
  }, [province]);

  // Estimate recipient's tax rate from their tradeBalance in KingdomMemberDto
  const estimatedTaxInfo = useMemo<EstimatedTaxInfo | null>(() => {
    if (!selectedTarget) return null;
    const target = kingdomMembers.find((m) => m.provinceId === selectedTarget);
    if (!target) return null;
    const networth = target.networth;
    // tradeBalance may be missing if cached DTO predates SF-83 — fall back to 0 (no tax)
    const tradeBalance = Number.isFinite(target.tradeBalance) ? target.tradeBalance : 0;
    const taxRate = estimateTaxRate(tradeBalance, networth);
    const receivedGold = Math.floor(resourceAmounts.gold * (1 - taxRate));
    const receivedFood = Math.floor(resourceAmounts.food * (1 - taxRate));
    const receivedSoldiers = Math.floor(resourceAmounts.soldiers * (1 - taxRate));
    const receivedRunes = Math.floor(resourceAmounts.runes * (1 - taxRate));
    return { taxRate, receivedGold, receivedFood, receivedSoldiers, receivedRunes };
  }, [selectedTarget, kingdomMembers, resourceAmounts]);

  // Validation states
  const hasResources = useMemo(() => {
    return Object.values(resourceAmounts).some((v) => v > 0);
  }, [resourceAmounts]);

  const isOverLimit = tradeValue > maxShipment;
  const isApproachingLimit = tradeValue > maxShipment * 0.9;

  const canSubmit = useMemo(() => {
    return Boolean(hasResources && selectedTarget) && !isOverLimit && !isSubmitting;
  }, [hasResources, selectedTarget, isOverLimit, isSubmitting]);

  // SF-154 Decision 4: extracted so the Recipient card's Retry action can re-invoke it directly,
  // bypassing the mount-only re-entry guard.
  async function loadKingdom() {
    if (!province || !worldId) return;
    const { island, kingdom } = province.metadata.location;
    const kingdomId = `${kingdom}:${island}`;

    setKingdomLoadError(null);
    setIsLoadingKingdom(true);
    try {
      const kingdomData = await getKingdom(worldId, kingdomId);
      setKingdomMembers(kingdomData.members);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load kingdom';
      setKingdomLoadError(message);
      reportError(message);
    } finally {
      setIsLoadingKingdom(false);
    }
  }

  // Auto-load own kingdom on mount
  useEffect(() => {
    if (!province || hasAutoLoadedKingdom || !worldId) return;
    setHasAutoLoadedKingdom(true);
    loadKingdom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province, hasAutoLoadedKingdom, worldId]);

  // Handle resource input changes — NumericInput's onChange already yields a parsed number.
  const handleResourceChange = (resource: keyof ResourceAmounts, value: number) => {
    if (value < 0) return;

    const available = availableResources[resource];
    const clamped = Math.min(value, available);

    setResourceAmounts((prev) => ({
      ...prev,
      [resource]: clamped,
    }));
  };

  // SF-154 Decision 2: headroom-aware Max fill — the most units of `resource` that can be added
  // without pushing the total shipment over `maxShipment`, given the other resources' amounts.
  const handleMaxFill = (resource: keyof ResourceAmounts) => {
    const amount = resolveMaxFill(resource, resourceAmounts, availableResources[resource], maxShipment);
    setResourceAmounts((prev) => ({ ...prev, [resource]: amount }));
    setResourceResetKey((key) => key + 1);
  };

  // SF-154 Decision 3: single header-level Clear-all replaces four per-row Clear buttons.
  const handleClearAll = () => {
    setResourceAmounts({ gold: 0, food: 0, soldiers: 0, runes: 0 });
    setResourceResetKey((key) => key + 1);
  };

  // Send aid
  const handleSendAid = async () => {
    setSubmitError(null);
    setAidResult(null);

    if (!province || !provinceId || !worldId) return;

    if (!selectedTarget) {
      reportError('Please select a target province');
      return;
    }

    if (!hasResources) {
      reportError('Please enter at least one resource to send');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await sendAid(worldId, provinceId, {
        targetProvinceId: selectedTarget,
        gold: resourceAmounts.gold,
        food: resourceAmounts.food,
        soldiers: resourceAmounts.soldiers,
        runes: resourceAmounts.runes,
      });

      setAidResult(response.result);

      if (response.result.success) {
        // Reset form
        setResourceAmounts({ gold: 0, food: 0, soldiers: 0, runes: 0 });
        setResourceResetKey((key) => key + 1);

        // Update province context with response data (includes updated trade balance)
        if (response.province) {
          updateProvince(response.province as Province);
        }
      }
    } catch (err) {
      reportError(err instanceof Error ? err.message : 'Failed to send aid');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error || 'Province data not available'} />
        </div>
      </div>
    );
  }

  // SF-154 Decision 5: a single always-current explanation of why Send Aid is disabled, mirroring
  // Thievery.tsx's submitDisabledReason pattern. Priority: no recipient → no resources → over
  // limit → null. The handleSendAid validation short-circuits above stay in place as
  // defense-in-depth (Thievery.tsx:268-271 precedent) — they're unreachable via the disabled
  // button, not dead code to remove.
  // Over-limit's reason is intentionally shorter than the AlertRow's message above the button —
  // the AlertRow already states the exact limit, so this caption doesn't repeat it verbatim.
  const submitDisabledReason: string | null = !selectedTarget
    ? 'Select a recipient to continue.'
    : !hasResources
    ? 'Enter at least one resource to send.'
    : isOverLimit
    ? 'Reduce resources — shipment exceeds the limit.'
    : null;

  const shellProps: AidShellProps = {
    province,
    availableResources,
    resourceAmounts,
    handleResourceChange,
    handleMaxFill,
    handleClearAll,
    resourceResetKey,
    selectedTarget,
    setSelectedTarget,
    // Excludes self — sending aid to your own province is meaningless.
    kingdomMembers: kingdomMembers.filter((m) => m.provinceId !== provinceId),
    isLoadingKingdom,
    kingdomLoadError,
    loadKingdom,
    tradeValue,
    maxShipment,
    estimatedTaxInfo,
    hasResources,
    isOverLimit,
    isApproachingLimit,
    canSubmit,
    submitDisabledReason,
    isSubmitting,
    submitError,
    handleSendAid,
    aidResult,
  };

  return platform === 'mobile'
    ? <MobileAidShell {...shellProps} />
    : <DesktopAidShell {...shellProps} />;
}
